import { useState } from 'react';
import { Copy, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import TESTIDS from '@/constants/testIds';

export function VariantCard({ variant, selected, onUse }) {
    const [copied, setCopied] = useState(false);
    if (!variant) return null;

    const handleCopy = async () => {
        try {
            const text = [variant.long || variant.short, (variant.hashtags || []).join(' ')]
                .filter(Boolean)
                .join('\n\n');
            await navigator.clipboard.writeText(text);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch (e) {
            console.error('clipboard failed', e);
        }
    };

    return (
        <div
            data-testid={TESTIDS.composerVariantCard}
            data-selected={selected ? 'true' : 'false'}
            className={`group flex flex-col gap-3 rounded-lg border bg-card p-4 shadow-sm transition-colors duration-150 ${
                selected
                    ? 'border-[hsl(var(--brand-accent-current)/0.55)] ring-2 ring-[hsl(var(--brand-accent-current)/0.35)]'
                    : 'border-border hover:border-[hsl(var(--brand-accent-current)/0.35)]'
            }`}
        >
            <div className="flex items-center justify-between">
                <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {variant.label?.replace('option_', 'Option ') || 'Option'}
                </div>
                <div className="text-[10px] font-mono text-muted-foreground">
                    {variant.long?.length || 0} chars
                </div>
            </div>
            <div>
                <div className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                    Short
                </div>
                <p className="mt-1 text-sm leading-6 text-foreground">
                    {variant.short}
                </p>
            </div>
            <div>
                <div className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                    Long
                </div>
                <p className="mt-1 whitespace-pre-wrap text-sm leading-6 text-foreground">
                    {variant.long}
                </p>
            </div>
            {variant.cta ? (
                <div className="text-xs text-muted-foreground">
                    <span className="font-medium uppercase tracking-wider">CTA: </span>
                    {variant.cta}
                </div>
            ) : null}
            {variant.hashtags?.length ? (
                <div className="flex flex-wrap gap-1">
                    {variant.hashtags.map((tag, i) => (
                        <span
                            key={i}
                            className="rounded bg-[hsl(var(--surface-2))] px-1.5 py-0.5 font-mono text-[11px] text-muted-foreground"
                        >
                            {tag}
                        </span>
                    ))}
                </div>
            ) : null}
            {variant.first_comment ? (
                <div className="rounded-md border border-dashed border-border bg-[hsl(var(--surface-2))] p-2">
                    <div className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                        First comment
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">{variant.first_comment}</p>
                </div>
            ) : null}
            <div className="mt-auto flex items-center justify-end gap-2">
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleCopy}
                    data-testid={TESTIDS.composerVariantCopyButton || 'composer-variant-copy-button'}
                >
                    {copied ? (
                        <Check className="mr-1 h-3 w-3" />
                    ) : (
                        <Copy className="mr-1 h-3 w-3" />
                    )}
                    {copied ? 'Copied' : 'Copy'}
                </Button>
                <Button
                    size="sm"
                    onClick={() => onUse?.(variant)}
                    data-testid={TESTIDS.composerVariantUseButton}
                >
                    Use this
                </Button>
            </div>
        </div>
    );
}
